import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Stack,
  IconButton,
  Alert,
  Paper,
  Divider,
  Chip,
} from '@mui/material';
import {
  CardGiftcard,
  FavoriteRounded,
  Close,
  Warning,
  Person,
  Schedule,
} from '@mui/icons-material';
import { issueCardManually } from './api/cupidCardAdminApi';
import { apiClient } from '../../config/api';

// ── Types ────────────────────────────────────────────────────────────────────

interface UserPreview {
  userId: number;
  name: string;
  age: number | null;
  region: string | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function getWeekSchedule() {
  const now = new Date();
  const day = now.getDay();
  const daysFromMonday = day === 0 ? 6 : day - 1;

  const monday = new Date(now);
  monday.setDate(now.getDate() - daysFromMonday);
  monday.setHours(11, 0, 0, 0);

  const thursday = new Date(monday);
  thursday.setDate(monday.getDate() + 3);

  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);

  const fmt = (d: Date) =>
    d.toLocaleString('ko-KR', {
      month: 'short',
      day: 'numeric',
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });

  const toISO = (d: Date) => {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  };

  return {
    issuedAt: fmt(monday),
    expiresAt: fmt(thursday),
    extendDeadlineAt: fmt(sunday),
    issuedAtISO: toISO(monday),
    expiresAtISO: toISO(thursday),
    extendDeadlineAtISO: toISO(sunday),
  };
}

const GRADIENT = 'linear-gradient(135deg, #EC4899 0%, #FF6B35 100%)';

// ── UserSelectionCard ────────────────────────────────────────────────────────

interface UserCardProps {
  label: string;
  idValue: string;
  onIdChange: (v: string) => void;
  preview: UserPreview | null;
  lookupLoading: boolean;
  onLookup: () => void;
  accentColor: string;
  bgColor: string;
  disabled: boolean;
}

function UserSelectionCard({
  label,
  idValue,
  onIdChange,
  preview,
  lookupLoading,
  onLookup,
  accentColor,
  bgColor,
  disabled,
}: UserCardProps) {
  return (
    <Paper
      elevation={0}
      sx={{
        flex: 1,
        p: 2.5,
        bgcolor: bgColor,
        border: '2px solid',
        borderColor: accentColor,
        borderRadius: 3,
        transition: 'box-shadow 0.2s',
        '&:hover': { boxShadow: `0 4px 20px ${accentColor}33` },
      }}
    >
      <Typography
        variant="subtitle2"
        fontWeight={800}
        sx={{ color: accentColor, mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}
      >
        <Person fontSize="small" />
        {label}
      </Typography>

      <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
        <TextField
          size="small"
          type="number"
          label="User ID"
          value={idValue}
          onChange={(e) => onIdChange(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && idValue && onLookup()}
          disabled={disabled}
          sx={{
            flex: 1,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              bgcolor: 'white',
              '&.Mui-focused fieldset': { borderColor: accentColor },
            },
            '& label.Mui-focused': { color: accentColor },
          }}
          inputProps={{ min: 1 }}
        />
        <Button
          variant="contained"
          size="small"
          onClick={onLookup}
          disabled={!idValue || lookupLoading || disabled}
          sx={{
            bgcolor: accentColor,
            '&:hover': { bgcolor: accentColor, filter: 'brightness(0.88)' },
            '&:disabled': { bgcolor: '#E0E0E0' },
            borderRadius: 2,
            minWidth: 60,
            fontWeight: 700,
            fontSize: '0.8rem',
          }}
        >
          {lookupLoading ? <CircularProgress size={14} color="inherit" /> : '조회'}
        </Button>
      </Stack>

      {preview ? (
        <Box
          sx={{
            p: 1.5,
            bgcolor: 'white',
            borderRadius: 2,
            border: '1px solid',
            borderColor: `${accentColor}44`,
          }}
        >
          <Typography variant="body2" fontWeight={700} color="text.primary">
            {preview.name}
            {preview.age != null && (
              <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                · {preview.age}세
              </Typography>
            )}
          </Typography>
          {preview.region && (
            <Typography variant="caption" color="text.secondary" display="block">
              📍 {preview.region}
            </Typography>
          )}
          <Typography variant="caption" color="text.disabled">
            ID: {preview.userId}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            p: 1.5,
            bgcolor: 'white',
            borderRadius: 2,
            border: '1px dashed',
            borderColor: `${accentColor}66`,
            textAlign: 'center',
          }}
        >
          <Typography variant="caption" color="text.disabled">
            User ID 입력 후 조회
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

// ── Main Dialog ──────────────────────────────────────────────────────────────

export default function ManualCardIssueDialog({ open, onClose, onSuccess }: Props) {
  const [recipientId, setRecipientId] = useState('');
  const [targetId, setTargetId] = useState('');
  const [recipientPreview, setRecipientPreview] = useState<UserPreview | null>(null);
  const [targetPreview, setTargetPreview] = useState<UserPreview | null>(null);
  const [recipientLoading, setRecipientLoading] = useState(false);
  const [targetLoading, setTargetLoading] = useState(false);
  const [issuedAt, setIssuedAt] = useState(() => getWeekSchedule().issuedAtISO);
  const [expiresAt, setExpiresAt] = useState(() => getWeekSchedule().expiresAtISO);
  const [extendDeadlineAt, setExtendDeadlineAt] = useState(() => getWeekSchedule().extendDeadlineAtISO);
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const resetForm = useCallback(() => {
    setRecipientId('');
    setTargetId('');
    setRecipientPreview(null);
    setTargetPreview(null);
    const fresh = getWeekSchedule();
    setIssuedAt(fresh.issuedAtISO);
    setExpiresAt(fresh.expiresAtISO);
    setExtendDeadlineAt(fresh.extendDeadlineAtISO);
    setLocalError(null);
  }, []);

  const handleClose = useCallback(() => {
    if (!submitting) {
      resetForm();
      onClose();
    }
  }, [submitting, resetForm, onClose]);

  const lookupUser = useCallback(
    async (userId: number, role: 'recipient' | 'target') => {
      const setLoading = role === 'recipient' ? setRecipientLoading : setTargetLoading;
      const setPreview = role === 'recipient' ? setRecipientPreview : setTargetPreview;

      setLoading(true);
      try {
        const res = await apiClient.get<{ result: any }>(
          `/api-admin/v1/gth-common/silver-connect/members/${userId}`
        );
        const d = res.result;
        const age = d.birthDate
          ? new Date().getFullYear() - new Date(d.birthDate).getFullYear()
          : null;
        setPreview({
          userId: d.userId ?? userId,
          name: d.name ?? '알 수 없음',
          age,
          region: d.residenceRegion
            ? `${d.residenceRegion.level1Name} ${d.residenceRegion.level2Name}`
            : null,
        });
      } catch {
        setPreview({ userId, name: '(조회 실패 — ID 직접 입력)', age: null, region: null });
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const handleSubmit = useCallback(async () => {
    setLocalError(null);
    const rId = Number(recipientId);
    const tId = Number(targetId);

    if (!rId || !tId) {
      setLocalError('수신자 ID와 발신자(타겟) ID를 모두 입력해주세요.');
      return;
    }
    if (rId === tId) {
      setLocalError('수신자와 발신자(타겟)는 서로 다른 사용자여야 합니다.');
      return;
    }
    if (!expiresAt || !extendDeadlineAt) {
      setLocalError('만료일시와 연장마감일은 필수입니다.');
      return;
    }

    const normalizeISO = (v: string) => (v.length === 16 ? `${v}:00` : v);

    setSubmitting(true);
    try {
      const result = await issueCardManually({
        recipientUserId: rId,
        targetUserId: tId,
        ...(issuedAt && { issuedAt: normalizeISO(issuedAt) }),
        expiresAt: normalizeISO(expiresAt),
        extendDeadlineAt: normalizeISO(extendDeadlineAt),
      });
      resetForm();
      onClose();
      onSuccess(
        `카드 수동 발급 완료 ✅ (카드 ID: ${result.cardId}, 수신자 ${rId} → 타겟 ${tId})`
      );
    } catch (err: any) {
      setLocalError(err?.message ?? '카드 발급에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setSubmitting(false);
    }
  }, [recipientId, targetId, issuedAt, expiresAt, extendDeadlineAt, onClose, onSuccess, resetForm]);

  const canSubmit = Boolean(recipientId) && Boolean(targetId) && !submitting;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: 'hidden',
          boxShadow: '0 24px 80px rgba(236, 72, 153, 0.18)',
        },
      }}
    >
      {/* ── Gradient Header ───────────────────────────────────────────────── */}
      <Box
        sx={{
          background: GRADIENT,
          px: 3,
          py: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 48,
              height: 48,
              bgcolor: 'rgba(255,255,255,0.22)',
              borderRadius: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backdropFilter: 'blur(4px)',
            }}
          >
            <CardGiftcard sx={{ color: 'white', fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={900} color="white" lineHeight={1.2}>
              큐피트 카드 수동 발급
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.82)' }}>
              관리자 권한으로 카드를 직접 발급합니다
            </Typography>
          </Box>
        </Stack>
        <IconButton onClick={handleClose} disabled={submitting} sx={{ color: 'rgba(255,255,255,0.85)' }}>
          <Close />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 3, bgcolor: '#F8F9FA' }}>
        <Stack spacing={3}>

          {/* ── Section 1: User Selection ──────────────────────────────────── */}
          <Box>
            <Typography
              variant="overline"
              color="text.secondary"
              fontWeight={700}
              sx={{ letterSpacing: 1.5, fontSize: 10, display: 'block', mb: 1.5 }}
            >
              👤 사용자 선택
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="stretch">
              <UserSelectionCard
                label="수신자 (카드를 받는 사람)"
                idValue={recipientId}
                onIdChange={setRecipientId}
                preview={recipientPreview}
                lookupLoading={recipientLoading}
                onLookup={() => recipientId && lookupUser(Number(recipientId), 'recipient')}
                accentColor="#EC4899"
                bgColor="#FDF2F8"
                disabled={submitting}
              />

              {/* Animated connector */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  px: { xs: 0, sm: 0.5 },
                }}
              >
                <FavoriteRounded
                  sx={{
                    color: '#EC4899',
                    fontSize: 32,
                    filter: 'drop-shadow(0 2px 6px rgba(236,72,153,0.4))',
                    '@keyframes heartbeat': {
                      '0%, 100%': { transform: 'scale(1)' },
                      '50%': { transform: 'scale(1.25)' },
                    },
                    animation: 'heartbeat 1.8s ease-in-out infinite',
                  }}
                />
              </Box>

              <UserSelectionCard
                label="발신자 · 타겟 (카드에 표시될 상대)"
                idValue={targetId}
                onIdChange={setTargetId}
                preview={targetPreview}
                lookupLoading={targetLoading}
                onLookup={() => targetId && lookupUser(Number(targetId), 'target')}
                accentColor="#FF6B35"
                bgColor="#FFF8F3"
                disabled={submitting}
              />
            </Stack>
          </Box>

          <Divider />

          {/* ── Section 2: Schedule (editable) ────────────────────────────── */}
          <Box>
            <Typography
              variant="overline"
              color="text.secondary"
              fontWeight={700}
              sx={{ letterSpacing: 1.5, fontSize: 10, display: 'block', mb: 1.5 }}
            >
              📅 발급 일정 (기본값: 이번 주 배치 사이클)
            </Typography>

            <Stack spacing={2}>
              <TextField
                label="발급일시 (선택)"
                type="datetime-local"
                size="small"
                fullWidth
                value={issuedAt}
                onChange={(e) => setIssuedAt(e.target.value)}
                disabled={submitting}
                InputLabelProps={{ shrink: true }}
                helperText="미입력 시 서버에서 현재 시각으로 자동 설정"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'white' } }}
              />
              <TextField
                label="만료일시 *"
                type="datetime-local"
                size="small"
                fullWidth
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                disabled={submitting}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#FFFBF0' },
                  '& label': { color: '#92400E' },
                  '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: '#D97706' },
                }}
              />
              <TextField
                label="연장마감일 *"
                type="datetime-local"
                size="small"
                fullWidth
                value={extendDeadlineAt}
                onChange={(e) => setExtendDeadlineAt(e.target.value)}
                disabled={submitting}
                InputLabelProps={{ shrink: true }}
                helperText="이 날짜까지 카드를 열지 않으면 영구 소멸"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'white' } }}
              />
            </Stack>
          </Box>

          {/* ── Error Alert ────────────────────────────────────────────────── */}
          {localError && (
            <Alert
              severity="error"
              onClose={() => setLocalError(null)}
              sx={{ borderRadius: 2 }}
            >
              {localError}
            </Alert>
          )}

          {/* ── Warning Banner ─────────────────────────────────────────────── */}
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              bgcolor: '#FFFBEB',
              border: '1px solid #FDE68A',
              borderRadius: 2,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
            }}
          >
            <Warning sx={{ color: '#D97706', fontSize: 18, mt: 0.15, flexShrink: 0 }} />
            <Typography variant="caption" color="#92400E" lineHeight={1.6}>
              수동 발급 카드는 <strong>정기 배치와 무관하게 즉시 생성</strong>됩니다.
              취소하려면 '강제 만료' 기능을 사용하세요.
              이 작업은 관리자 로그에 기록됩니다.
            </Typography>
          </Paper>
        </Stack>
      </DialogContent>

      {/* ── Footer Actions ────────────────────────────────────────────────── */}
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          bgcolor: 'white',
          borderTop: '1px solid #F0F0F0',
          gap: 1.5,
        }}
      >
        <Button
          onClick={handleClose}
          disabled={submitting}
          variant="outlined"
          sx={{ borderRadius: 2, fontWeight: 700, px: 3 }}
        >
          취소
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!canSubmit}
          variant="contained"
          startIcon={
            submitting ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <CardGiftcard />
            )
          }
          sx={{
            borderRadius: 2,
            fontWeight: 900,
            px: 4,
            py: 1.2,
            fontSize: '0.95rem',
            background: canSubmit ? GRADIENT : undefined,
            boxShadow: canSubmit ? '0 4px 16px rgba(236, 72, 153, 0.35)' : undefined,
            '&:hover': {
              background: GRADIENT,
              filter: 'brightness(0.93)',
              boxShadow: '0 6px 24px rgba(236, 72, 153, 0.45)',
            },
          }}
        >
          {submitting ? '발급 중...' : '💝  카드 발급하기'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
